import {Modal, Image} from 'antd-mobile';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {cn, toPriceStr} from '@/utils';
import BackgroundTimer from '@components/utils/timer';
const spinIcon = require('@/assets/icons/vip/v2/spin.webp');
const scratchCardIcon = require('@/assets/icons/vip/v2/scratch.webp');
const moneyIcon = require('@/assets/icons/vip/v2/money.webp');
import styles from '../vip.module.scss';
import {useTranslation} from 'react-i18next';
interface ReceivedModalProps {
  className?: string;
}

export interface ReceivedModalRef {
  show: (prize: {spin: number; scratchCard: number; money: number}) => void;
}

const PrizeItem = ({
  type,
  count,
}: {
  type: 'spin' | 'scratchCard' | 'money';
  count: number;
}) => {
  const icon = useMemo(() => {
    const iconMap = {
      spin: spinIcon,
      scratchCard: scratchCardIcon,
      money: moneyIcon,
    };
    return iconMap[type];
  }, [type]);
  return (
    <div
      className="flex flex-col size-[4.25rem] border border-[#468B66] items-center relative overflow-hidden rounded"
      style={{
        background: 'linear-gradient(180deg, #EEC051 0%, #FEF58C 50%)',
        boxShadow: '0px 2px 2px 0px rgba(255, 255, 255, 0.25) inset',
      }}>
      <Image src={icon} className="size-9 mt-2" />
      <span
        className="text-xs font-semibold flex items-center justify-center absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-b from-[#468B66] to-[#56BD85] text-[#FFF701] [text-shadow:_0_0_1px_#084725,0_0_1px_#084725,0_0_1px_#084725,0_0_1px_#084725]"
        style={{
          boxShadow:
            '0px -1px 1px 0px rgba(255, 255, 255, 0.10) inset, 0px -1px 2px 0px rgba(0, 0, 0, 0.20)',
        }}>
        {type === 'money'
          ? toPriceStr(count, {fixed: 0, suffixUnit: 'K'})
          : `${count}`}
      </span>
    </div>
  );
};

const ReceivedModal = forwardRef<ReceivedModalRef, ReceivedModalProps>(
  ({className}, ref) => {
    const [visible, setVisible] = useState(false);
    const [prize, setPrize] = useState<{
      spin: number;
      scratchCard: number;
      money: number;
    }>({
      spin: 0,
      scratchCard: 0,
      money: 0,
    });
    useImperativeHandle(ref, () => ({
      show: _prize => {
        setPrize(_prize);
        setVisible(true);
      },
    }));

    const [time, setTime] = useState(3);
    const timer = useRef<number>();
    const {i18n} = useTranslation();
    const handleClose = useCallback(() => {
      if (timer.current) {
        BackgroundTimer.clearInterval(timer.current);
        timer.current = undefined;
      }
      setVisible(false);
    }, []);

    useEffect(() => {
      if (visible) {
        setTime(3);
        timer.current = BackgroundTimer.setInterval(() => {
          setTime(v => v - 1);
        }, 1000);
      }
      return () => {
        if (timer.current) {
          BackgroundTimer.clearInterval(timer.current);
          timer.current = undefined;
        }
      };
    }, [visible]);

    useEffect(() => {
      if (time <= 0) {
        handleClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    const congratulationsImg = useMemo(() => {
      const lang = i18n.language;
      const imgMap = {
        en_US: require('@/assets/icons/vip/v2/congratulations-text.webp'),
        hi_IN: require('@/assets/icons/vip/v2/congratulations-text-hi.webp'),
        te_IN: require('@/assets/icons/vip/v2/congratulations-text-te.webp'),
        ml_IN: require('@/assets/icons/vip/v2/congratulations-text-ml.webp'),
        ta_IN: require('@/assets/icons/vip/v2/congratulations-text-ta.webp'),
      };
      return imgMap[lang as keyof typeof imgMap] || imgMap.en_US;
    }, [i18n]);

    return (
      <Modal
        className="[&_div.adm-center-popup-wrap]:top-[calc(50%-6.875rem)]"
        bodyClassName={cn(
          'bg-transparent [&_div.adm-modal-content]:px-0',
          className,
        )}
        getContainer={() => document.body}
        visible={visible}
        onClose={handleClose}
        showCloseButton={false}
        content={
          <div
            className={cn(
              'w-[23.4375rem] h-[22.125rem] relative bg-full bg-no-repeat',
              visible ? styles['animate-spring-down'] : '',
            )}
            style={{
              backgroundImage: `url(${require('@/assets/imgs/vip/v2/level-bg.webp')})`,
            }}>
            <div
              className="absolute left-1/2 top-[10.8125rem] -translate-x-1/2 bg-contain bg-center bg-no-repeat w-[7.5rem] h-[.9375rem]"
              style={{
                backgroundImage: `url(${congratulationsImg})`,
              }}
            />
            {/* 金额 */}
            <div className="absolute bottom-[4.0625rem] left-0 right-0 flex flex-row justify-center items-center gap-7">
              {!!prize.spin && <PrizeItem type="spin" count={prize.spin} />}
              {!!prize.scratchCard && (
                <PrizeItem type="scratchCard" count={prize.scratchCard} />
              )}
              {!!prize.money && <PrizeItem type="money" count={prize.money} />}
            </div>
          </div>
        }
        closeOnMaskClick
      />
    );
  },
);

export default ReceivedModal;
